import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Switch from 'antd/lib/switch';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import {
    selectAddFixRequestsToOpenPRsAgainstReleaseBranches,
    selectAppSettings,
    selectThreadFixNotifications,
    selectNotificationIntegration,
    selectPreventFixRequestsAfterSubmission,
    selectPreventFixRequestsAfterSubmissionCustomCopy,
    selectPreventFixRequestsAfterSubmissionCustomCopyEnabled,
} from '@/selectors/selectors';
import { RootState } from 'reducers/reducers';
import { AutoCherryPickMatchTokenOn, IntegrationType } from '@/types';
import { cherryPickPatternValidator } from '@/helpers';
import { Tooltip } from 'antd/lib';
import { useWatch } from 'antd/es/form/Form';
import { antGrey } from '../styles/globalStyles';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useFixSettingsModal } from './useFixSettingsModal';

interface EditOpenPRFixesModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditOpenPRFixesModal: React.FC<EditOpenPRFixesModalProps> = (props: EditOpenPRFixesModalProps) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));
    const allowFixesWithToken = appSettings.fixesTokenConfig?.enabled ?? false;
    const fixRequestTokens = appSettings.fixesTokenConfig?.fixRequestTokens ?? ['[runway:fix-{version}]'];
    const fixRequestTokenMatchOn = appSettings.fixesTokenConfig?.matchTokenOn ?? [AutoCherryPickMatchTokenOn.Title];
    const threadFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const preventFixRequestsAfterSubmission = useSelector(selectPreventFixRequestsAfterSubmission(props.appId));
    const preventFixRequestsAfterSubmissionCustomCopy = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopy(props.appId),
    );
    const preventFixRequestsAfterSubmissionCustomCopyEnabled = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopyEnabled(props.appId),
    );
    const notificationIntegration = useSelector((state: RootState) =>
        selectNotificationIntegration(state, props.appId),
    );

    // threading is only supported in Slack
    const hasSlackIntegration =
        notificationIntegration != null ? notificationIntegration.id === IntegrationType.Slack : false;

    const isPreventFixRequestsAfterSubmissionEnabled = useWatch(['preventFixRequestsAfterSubmission'], form);
    const isPreventFixRequestsAfterSubmissionCustomCopyEnabled = useWatch(
        ['preventFixRequestsAfterSubmissionCustomCopyEnabled'],
        form,
    );
    const isAllowFixesWithTokenEnabled = useWatch(['allowFixesWithToken'], form);

    const initialValues = {
        addFixRequestsToOpenPRsAgainstReleaseBranches: addFixRequestsToOpenPRsAgainstReleaseBranches,
        allowFixesWithToken,
        fixRequestTokens,
        fixRequestTokenMatchOn,
        preventFixRequestsAfterSubmission: preventFixRequestsAfterSubmission,
        preventFixRequestsAfterSubmissionCustomCopy: preventFixRequestsAfterSubmissionCustomCopy,
        preventFixRequestsAfterSubmissionCustomCopyEnabled: preventFixRequestsAfterSubmissionCustomCopyEnabled,
        threadFixNotifications: threadFixNotifications,
    };

    const onFinish = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            addFixRequestsToOpenPRsAgainstReleaseBranches: value.addFixRequestsToOpenPRsAgainstReleaseBranches,
            fixesTokenConfig: {
                enabled: value.allowFixesWithToken,
                fixRequestTokens: value.fixRequestTokens,
                matchTokenOn: value.fixRequestTokenMatchOn,
            },
            preventFixRequestsAfterSubmission: value.preventFixRequestsAfterSubmission,
            preventFixRequestsAfterSubmissionCustomCopyEnabled:
                value.preventFixRequestsAfterSubmissionCustomCopyEnabled,
            preventFixRequestsAfterSubmissionCustomCopy: value.preventFixRequestsAfterSubmissionCustomCopy,
            // Only Slack supports threading, so the field is omitted (left untouched) otherwise.
            ...(hasSlackIntegration ? { threadFixNotifications: value.threadFixNotifications } : {}),
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    return (
        <Modal
            title="Fixes for open PRs"
            onOk={form.submit}
            okButtonProps={{ loading: isLoading }}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={afterClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    Configure when Runway automatically creates fixes from pull requests.
                </Text>
                <Form
                    onFinish={onFinish}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="addFixRequestsToOpenPRsAgainstReleaseBranches"
                        valuePropName="checked"
                        label={'Create a fix for all open PRs against the release branch'}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item name="allowFixesWithToken" valuePropName="checked" style={{ marginBottom: 4 }}>
                        <Checkbox>Allow PRs to be flagged as fixes using a token</Checkbox>
                    </Form.Item>
                    <Form.Item
                        name="fixRequestTokens"
                        label="Fix token patterns"
                        style={{ marginLeft: 24 }}
                        tooltip="Include a string to flag a PR as a fix. Pattern must include {version}, or {versionConcise}, or no tokens at all."
                        rules={[
                            { required: isAllowFixesWithTokenEnabled },
                            {
                                validator: (_, value) =>
                                    isAllowFixesWithTokenEnabled ?
                                        cherryPickPatternValidator(value)
                                    :   Promise.resolve(),
                            },
                        ]}
                    >
                        <Select
                            style={{ width: '100%' }}
                            allowClear
                            mode="tags"
                            notFoundContent={null}
                            placeholder="Select"
                            disabled={!isAllowFixesWithTokenEnabled}
                        />
                    </Form.Item>
                    <Form.Item
                        name="fixRequestTokenMatchOn"
                        label="Match token on..."
                        style={{ marginLeft: 24 }}
                        rules={[{ required: isAllowFixesWithTokenEnabled }]}
                    >
                        <Select
                            style={{ width: '100%' }}
                            allowClear
                            showSearch={false}
                            mode="multiple"
                            placeholder="Select"
                            disabled={!isAllowFixesWithTokenEnabled}
                            options={[
                                { value: AutoCherryPickMatchTokenOn.Title, label: 'PR title' },
                                { value: AutoCherryPickMatchTokenOn.Label, label: 'PR label' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        name="preventFixRequestsAfterSubmission"
                        valuePropName="checked"
                        style={{ marginBottom: 4 }}
                    >
                        <Checkbox>Disallow new fix requests once release is submitted</Checkbox>
                    </Form.Item>
                    {isPreventFixRequestsAfterSubmissionEnabled ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopyEnabled"
                            valuePropName="checked"
                            style={{ marginBottom: 4, marginLeft: 12 }}
                        >
                            <Checkbox>Show a message alongside disallowed action</Checkbox>
                        </Form.Item>
                    :   null}
                    {(
                        isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                        isPreventFixRequestsAfterSubmissionEnabled
                    ) ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopy"
                            style={{ marginLeft: 12 }}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (
                                            isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                                            isPreventFixRequestsAfterSubmissionEnabled &&
                                            !value
                                        ) {
                                            return Promise.reject(new Error('Custom copy is required'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input.TextArea rows={5} />
                        </Form.Item>
                    :   null}
                    {hasSlackIntegration && (
                        <Form.Item name="threadFixNotifications" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>
                                Thread related fix notifications
                                <Tooltip title="Notifications relevant to a given fix will be threaded in Slack">
                                    <InfoCircleOutlined
                                        style={{ color: antGrey.grey7, marginLeft: 8 }}
                                        onClick={(e) => e.preventDefault()}
                                    />
                                </Tooltip>
                            </Checkbox>
                        </Form.Item>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default EditOpenPRFixesModal;
